import React, { useEffect, useMemo, useState } from 'react'
import { BookOpen, Share2 } from 'lucide-react'
import BookCover from './BookCover'
import PillButton from './PillButton'
import { HStack } from '@astryxdesign/core/Layout'
import { Link } from '@astryxdesign/core/Link'
import { Timestamp } from '@astryxdesign/core/Timestamp'
import { Token } from '@astryxdesign/core/Token'
import { useToast } from '@astryxdesign/core/Toast'
import LandingHeader from './LandingHeader'
import SaveStoryButton from './SaveStoryButton'
import StarRating from './StarRating'
import StoryReviews from './StoryReviews'
import SettingsPanel, { type SettingsSection } from './SettingsPanel'
import { authClient } from '../lib/auth-client'
import { rememberRecentlyReadStory } from '../lib/recentlyRead'
import { sanitizePublishedHtml } from '../lib/sanitizePublishedHtml'
import type { AppSettings } from '../App'

interface PublishedStoryViewProps {
  publicationId: string
  slug: string
  version?: number
}

const GENRE_COLORS = ['blue', 'purple', 'pink', 'teal', 'orange'] as const

export default function PublishedStoryView({
  publicationId,
  slug,
  version
}: PublishedStoryViewProps): React.JSX.Element {
  const showToast = useToast()
  const { data: session } = authClient.useSession()
  const [story, setStory] = useState<PublishedStory | null>(null)
  const [error, setError] = useState('')
  const [authorProfile, setAuthorProfile] = useState<AuthorProfile | null>(null)
  const [isProfileLoaded, setIsProfileLoaded] = useState(false)
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('account')
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    if (!session?.user) return
    let active = true
    Promise.all([window.api.profiles.getActive(), window.api.settings.get()])
      .then(([profile, loadedSettings]) => {
        if (!active) return
        setAuthorProfile(profile)
        setSettings(loadedSettings)
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setIsProfileLoaded(true)
      })
    return () => {
      active = false
    }
  }, [session?.user])

  const openSettings = (section: 'account' | 'profile'): void => {
    setSettingsSection(section)
    void window.api.settings
      .get()
      .then((latestSettings) => {
        setSettings(latestSettings)
        setShowSettings(true)
      })
      .catch(() => setShowSettings(true))
  }

  const saveSettings = (updatedSettings: AppSettings): void => {
    setSettings(updatedSettings)
    localStorage.setItem('ninenights-settings', JSON.stringify(updatedSettings))
    void window.api.settings.save(updatedSettings)
  }

  const syncTheme = (theme: AppSettings['theme']): void => {
    setSettings((current) => (current ? { ...current, theme } : current))
  }

  const saveProfile = async (changes: {
    penName: string
    authorBlurb: string
    libraryPublic: boolean
  }): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.update(authorProfile.id, changes)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const uploadAvatar = async (file: File): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.uploadAvatar(authorProfile.id, file)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const removeAvatar = async (): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.removeAvatar(authorProfile.id)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const selectDefaultAvatar = async (defaultAvatarId: string): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.selectDefaultAvatar(
      authorProfile.id,
      defaultAvatarId
    )
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const accountDisplayName =
    authorProfile?.penName.trim() || session?.user?.name?.trim() || undefined
  const isAccountLoading = Boolean(session?.user) && !isProfileLoaded

  useEffect(() => {
    let active = true
    // Resetting state ahead of a fetch keyed on route params, same pattern as
    // ChapterHistoryDialog and StoryCanvas.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStory(null)
    setError('')
    window.api.publishedStories
      .load(publicationId, slug, version)
      .then((loaded) => {
        if (active) {
          setStory(loaded)
          rememberRecentlyReadStory(loaded.id)
        }
      })
      .catch((reason) => {
        if (active)
          setError(reason instanceof Error ? reason.message : 'Unable to load this story.')
      })
    return () => {
      active = false
    }
  }, [publicationId, slug, version])

  // The server answers 401 "Sign in to read this story." only for a restricted story with no
  // session, so this is the one error a sign-in can fix.
  const needsSignIn = error === 'Sign in to read this story.' && !session?.user

  const safeContent = useMemo(() => (story ? sanitizePublishedHtml(story.content) : ''), [story])

  const shareStory = async (): Promise<void> => {
    if (!story) return
    const url = window.location.href
    try {
      if (navigator.share) {
        await navigator.share({ title: story.title, text: story.blurb, url })
      } else {
        await navigator.clipboard.writeText(url)
        showToast({ body: 'Story link copied', type: 'info' })
      }
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === 'AbortError') return
      showToast({ body: 'Could not share the story link.', type: 'error' })
    }
  }

  const header = (
    <LandingHeader
      showNavLinks={false}
      showAppButton={false}
      isAppHeader
      isAccountLoading={isAccountLoading}
      userName={accountDisplayName}
      avatarUrl={authorProfile?.avatarUrl}
      onOpenSettings={openSettings}
      currentTheme={settings?.theme}
      onThemeChange={syncTheme}
    />
  )

  const settingsModal = showSettings && settings && (
    <SettingsPanel
      settings={settings}
      profile={authorProfile}
      initialSection={settingsSection}
      onSaveSettings={saveSettings}
      onSaveProfile={saveProfile}
      onUploadAvatar={uploadAvatar}
      onRemoveAvatar={removeAvatar}
      onSelectDefaultAvatar={selectDefaultAvatar}
      onClose={() => setShowSettings(false)}
    />
  )

  if (error) {
    return (
      <main className="published-story-shell">
        {header}
        <div className="published-story-state">
          <BookOpen size={32} />
          <h1>{needsSignIn ? 'Sign in to read this story' : 'Story unavailable'}</h1>
          <p>
            {needsSignIn
              ? 'The author shared this story with specific readers. Sign in with the email they used to open it.'
              : error}
          </p>
          {needsSignIn && (
            <PillButton
              variant="primary"
              label="Sign in"
              onClick={() => {
                window.location.href = '/'
              }}
            />
          )}
        </div>
        {settingsModal}
      </main>
    )
  }

  if (!story) {
    return (
      <main className="published-story-shell">
        {header}
        <div className="published-story-state">
          <p>Loading story…</p>
        </div>
        {settingsModal}
      </main>
    )
  }

  return (
    <main className="published-story-shell">
      {header}
      <article className="published-story">
        {story.coverUrl && (
          <BookCover
            size="md"
            coverUrl={story.coverUrl}
            title={story.title}
            authorName={story.authorName}
            className="published-story-cover"
          />
        )}
        <h1>{story.title}</h1>
        <p className="published-story-blurb">{story.blurb}</p>
        {story.genre && (
          <HStack gap={2} wrap="wrap" className="published-story-genres">
            {story.genre
              .split(',')
              .map((genre) => genre.trim())
              .filter(Boolean)
              .map((genre, index) => (
                <Token
                  key={genre}
                  label={genre}
                  size="sm"
                  color={GENRE_COLORS[index % GENRE_COLORS.length]}
                />
              ))}
          </HStack>
        )}
        <p className="published-story-byline">
          By{' '}
          <Link href={`/profiles/${encodeURIComponent(story.authorProfileId)}`}>
            {story.authorName}
          </Link>
        </p>
        <HStack gap={2} wrap="wrap" align="center" className="published-story-metadata">
          <span>{story.readingTimeMinutes} min read</span>
          <span aria-hidden="true">·</span>
          <Timestamp value={Math.floor(story.publishedAt / 1000)} format="date" />
          <PillButton
            variant="secondary"
            size="sm"
            label="Share"
            icon={<Share2 size={14} />}
            onClick={() => void shareStory()}
          />
          <SaveStoryButton
            publicationId={story.id}
            isSaved={story.viewerSaved}
            saveCount={story.saveCount}
            isSignedIn={Boolean(session?.user)}
            size="sm"
            onSavedChange={(next) =>
              setStory((current) =>
                current
                  ? { ...current, viewerSaved: next.saved, saveCount: next.saveCount }
                  : current
              )
            }
          />
          {story.ratingCount > 0 && (
            <a href="#story-reviews" className="published-story-rating-link">
              <HStack gap={1} align="center">
                <StarRating value={Math.round(story.ratingAverage ?? 0)} size={14} />
                <span>
                  {story.ratingAverage} ({story.ratingCount})
                </span>
              </HStack>
            </a>
          )}
        </HStack>
        <section
          className="published-story-manuscript"
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />
        <StoryReviews
          publicationId={story.id}
          isOwnStory={story.isOwnStory}
          isSignedIn={Boolean(session?.user)}
          viewerRating={story.viewerRating}
          ratingAverage={story.ratingAverage}
          ratingCount={story.ratingCount}
          onAggregateChange={(next) =>
            setStory((current) => (current ? { ...current, ...next } : current))
          }
        />
      </article>
      {settingsModal}
    </main>
  )
}
